import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import TablePlaceholder from "../../../SharedComponents/Loading/TablePlaceholder";
import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import { CreditCardIcon } from "@heroicons/react/solid";
import BillingRow from "./BillingRow";
import { BUTTON_VARIANT_TYPES } from "../../../../utils/constants";
import Button from "../../../SharedComponents/Button";
import useMountStatus from '../../../../hooks/useMountStatus';
import Alert, { ALERT_TYPE } from "../../../Auth/Alert";

function Billing() {
    const { searchTerm } = useParams();

    const [alert, setAlert] = useState('');
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [initialTransactions, setInitialTransactions] = useState([]);
    const [transactions, setTransactions] = useState(initialTransactions);
    const [transactionsLoading, setTransactionsLoading] = useState(false);

    const unmounted = useMountStatus();

    const searchInput = useRef(null);

    const search = useCallback((term) => {
        return initialTransactions.filter(({ userName, name }) => {
            return userName.toLowerCase().indexOf(term.toLowerCase()) > -1 ||
                name.toLowerCase().indexOf(term.toLowerCase()) > -1;
        })
    }, [initialTransactions]);

    const handleKeyPress = (event) => {
        const filteredTransactions = search(event.target.value);

        if (event.target.value) {
            setTransactions(filteredTransactions);
        } else {
            setTransactions(initialTransactions);
        }
    };


    const fetchTransactions = useCallback(async () => {
        setTransactionsLoading(true);

        try {
            const sampleTransactions = [
                {
                    date: "12/12/2021",
                    payee: "Psych Press",
                    type: "DEBIT",
                    method: "CREDIT CARD",
                    status: "SUCCESSFUL",
                    amount: "$399.95",
                    invoice: "PROCESSING"
                },
                {
                    date: "4/11/2021",
                    payee: "Psych Press",
                    type: "DEBIT",
                    method: "BANK TRANSFER",
                    status: "PENDING",
                    amount: "$4555.95",
                    invoice: "https://link"
                },
                {
                    date: "5/12/2021",
                    payee: "Psych Press",
                    type: "DEBIT",
                    method: "PAY PAL",
                    status: "AUTHORISED",
                    amount: "$8964.65",
                    invoice: "https://link"
                },
                {
                    date: "12/11/2021",
                    payee: "Psych Press",
                    type: "CREDIT",
                    method: "CREDIT CARD",
                    status: "SUCCESSFUL",
                    amount: "$399.95",
                    invoice: "https://link"
                },
                {
                    date: "12/11/2021",
                    payee: "Psych Press",
                    type: "DEBIT",
                    method: "CREDIT CARD",
                    status: "FAILED",
                    amount: "$399.95",
                    invoice: "UNAVAILABLE"
                }
            ];

            if (unmounted) return;
            setInitialTransactions(sampleTransactions);
            setTransactions(sampleTransactions);
        } catch (err) {
            if (unmounted) return;
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setTransactionsLoading(false);
    }, [unmounted]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    useEffect(() => {
        if (searchTerm !== undefined) {
            setTransactions(search(searchTerm));
            searchInput.current.value = searchTerm;
        }

    }, [initialTransactions, search, searchTerm]);

    return (
        <DashboardContainer title={"Billing"} titleIcon={<CreditCardIcon className="block h-5 w-5 mr-2" />}>
            <Alert
                text={alert}
                type={alertType}
                active={alert}
                close={() => setAlert("")}
            />
            <div className={"grid grid-flow-row lg:grid-cols-6 gap-4"}>
                <div className={
                    "col-span-2 grid grid-flow-row gap-2 w-full mx-auto rounded-md px-3 py-3 " +
                    "bg-gradient-to-l from-primary-600 to-secondary-600 shadow-sm"
                }
                >
                    <div className="flex flex-col gap-4 justify-between font-medium">
                        <span className={"text-md text-secondary-100"}>Unpaid balance</span>
                        <div className={"text-2xl text-white flex items-center"}>
                            <span>$500 </span>
                            {/* <span className={"text-red-500 ml-1"}> OVERDUE</span> */}
                        </div>
                        <span className={"text-md text-secondary-100"}>Due: 13/13/2021</span>
                        <Button colour={BUTTON_VARIANT_TYPES.AMBER} text="Pay now" />
                    </div>
                </div>
                <div className={
                    "col-span-2 grid grid-flow-row gap-2 w-full mx-auto rounded-md px-3 py-3 " +
                    "bg-gradient-to-l from-primary-50 to-secondary-50 shadow-sm"
                }
                >
                    <div className="flex flex-col gap-4 justify-between font-medium">
                        <span className={"text-md text-indigo-900"}>Preferred Payment Method</span>
                        <div className={"text-md text-secondary-600 flex items-center"}>
                            <CreditCardIcon className="h-5 w-5 mr-1.5" /> Credit Card
                        </div>
                        <Button
                            text="Change preferred payment method"
                            extraStyling={
                                "text-white focus:ring-inset focus:ring-1 bg-secondary-500 " +
                                "hover:bg-secondary-600 focus:outline-none focus:ring-secondary-500"
                            }
                        />
                    </div>
                </div>
                <div className={
                    "col-span-2 grid grid-flow-row gap-2 w-full mx-auto rounded-md px-3 py-3 " +
                    "bg-gradient-to-r from-primary-50 to-secondary-50 shadow-sm"
                }
                >
                    <div className="flex flex-col gap-4 justify-between font-medium">
                        <span className={"text-md text-indigo-900"}>Payment plan</span>
                        <div className={"text-md text-primary-600"}>
                            Pay-as-you-go
                        </div>
                        <div className={"text-sm text-blue-gray-500 font-normal"}>
                            Want to try a different payment plan? To change to a different payment plan please
                            <a href={"/help-and-support"} className={"text-primary-600"}> <u>contact us</u></a>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col mt-4">
                <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="align-middle inline-block min-w-full sm:px-6 lg:px-8">
                        <div className={"mb-4"}>
                            <input
                                ref={searchInput}
                                type="text"
                                name="search"
                                id="search"
                                placeholder={"Search transactions"}
                                onChange={handleKeyPress}
                                className={
                                    "p-4 mt-1 h-12 border border-transparent focus:outline-none focus:ring-2" +
                                    "focus:ring-primary-600 focus:border-transparent block w-full shadow-sm rounded-lg"
                                }
                            />
                        </div>
                        <div className="overflow-hidden border-b border-none shadow-sm rounded-lg">
                            <table className="table-auto min-w-full shadow-sm">
                                <thead className="bg-blue-gray-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-left text-xs font-medium text-blue-gray-500
                                            uppercase font-mono tracking-wider hidden lg:table-cell"
                                        >
                                            Date
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-left text-xs font-medium text-blue-gray-500
                                            uppercase font-mono tracking-wider"
                                        >
                                            Paid to
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-left text-xs font-medium text-blue-gray-500
                                            uppercase font-mono tracking-wider hidden lg:table-cell"
                                        >
                                            Method
                                        </th>

                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-left text-xs font-medium text-blue-gray-500
                                            uppercase font-mono tracking-wider hidden lg:table-cell"
                                        >
                                            Type
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-left text-xs font-medium text-blue-gray-500
                                            uppercase font-mono tracking-wider"
                                        >
                                            Amount
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-left text-xs font-medium text-blue-gray-500
                                            uppercase font-mono tracking-wider"
                                        >
                                            Status
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-left text-xs font-medium text-blue-gray-500
                                            uppercase font-mono tracking-wider hidden lg:table-cell"
                                        >
                                            Invoice
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white shadow-sm">

                                    {transactionsLoading ?
                                        <TablePlaceholder columns={8} />
                                        :
                                        transactions.length !== 0 ?
                                            transactions.map((transaction, i) => (
                                                <BillingRow key={i} transaction={transaction} />
                                            ))
                                            :
                                            <tr>
                                                <td colSpan={8}>
                                                    <div className={"px-4 py-4 ml-2"}>
                                                        Sorry there were no results for your search
                                                    </div>
                                                </td>
                                            </tr>
                                    }

                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardContainer>
    )
}

export default Billing;
